import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api } from '../../lib/api'

interface RentalAttention {
  id: string
  actualStartAt: string
  pricingMode: string
  basePrice: string
  priceOverride: string | null
  priceOverrideReason: string | null
  discountPercent: string | null
  prepaymentAmount: string | null
  status: string
  customerName: string | null
  managerNotes: string | null
  customer: { phone: string }
  manager: { name: string }
  pickupPoint: { name: string }
  lines: Array<{ bike: { code: string; model?: string | null } }>
}

export default function AttentionPage() {
  const qc = useQueryClient()
  const navigate = useNavigate()

  const { data: rentals = [], isLoading } = useQuery<RentalAttention[]>({
    queryKey: ['attention-rentals'],
    queryFn: () =>
      api.get('/rentals', { params: { requiresAttention: 'true', limit: '200' } }).then((r) => r.data.rentals ?? []),
    staleTime: 45_000,
    refetchInterval: 60_000,
  })

  const confirm = useMutation({
    mutationFn: (id: string) => api.post(`/rentals/${id}/confirm-attention`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['attention-rentals'] })
      qc.invalidateQueries({ queryKey: ['stats'] })
    },
  })

  const bikeCodes = (r: RentalAttention) =>
    r.lines.length > 0 ? r.lines.map((l) => `#${l.bike.code}`).join(', ') : '—'

  const fmtDate = (iso: string) =>
    new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })

  const fmtPrice = (v: string | null | undefined) =>
    v != null ? `${Number(v).toLocaleString('ru-RU')} ₽` : '—'

  if (isLoading) return <div className="p-6 text-center text-gray-400">Загрузка...</div>

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto">
      <h1 className="text-xl font-bold text-gray-900 mb-4">Требует внимания</h1>

      {rentals.length === 0 && (
        <div className="text-center text-gray-400 py-12">Нет аренд, требующих проверки</div>
      )}

      <div className="space-y-3">
        {rentals.map((r) => {
          const openedNotQr = r.managerNotes?.includes('Открыто не по QR') ?? false
          const closedNotQr = r.managerNotes?.includes('Закрыто не по QR') ?? false
          return (
          <div
            key={r.id}
            onClick={() => navigate('/owner/rentals', { state: { openRentalId: r.id } })}
            className="bg-white rounded-2xl border border-amber-200 p-4 space-y-2 cursor-pointer hover:border-amber-300 transition-colors"
          >
            <div className="flex items-start justify-between">
              <div>
                <span className="text-sm font-bold text-gray-900">{bikeCodes(r)}</span>
                <span className="text-xs text-gray-400 ml-2">{fmtDate(r.actualStartAt)}</span>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${r.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                {r.status === 'active' ? 'Активна' : 'Завершена'}
              </span>
            </div>

            {(openedNotQr || closedNotQr) && (
              <div className="flex flex-wrap gap-1.5">
                {openedNotQr && <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium">📷 Открыто не по QR</span>}
                {closedNotQr && <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium">📷 Закрыто не по QR</span>}
              </div>
            )}

            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-gray-600">
              <div>Клиент: <span className="font-medium text-gray-900">{r.customerName || r.customer.phone}</span></div>
              <div>Менеджер: <span className="font-medium text-gray-900">{r.manager.name}</span></div>
              <div>Точка: <span className="font-medium text-gray-900">{r.pickupPoint.name}</span></div>
              <div>Режим: <span className="font-medium text-gray-900">{r.pricingMode === 'hourly' ? 'Часовой' : 'Суточный'}</span></div>
              <div>Тариф: <span className="font-medium text-gray-900">{fmtPrice(r.basePrice)}</span></div>
              <div>Предоплата: <span className="font-medium text-gray-900">{fmtPrice(r.prepaymentAmount)}</span></div>
            </div>

            {r.priceOverride != null && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                <p className="text-sm font-medium text-amber-800">Ручная цена: {fmtPrice(r.priceOverride)}</p>
                {r.priceOverrideReason && <p className="text-xs text-amber-600 mt-0.5">{r.priceOverrideReason}</p>}
              </div>
            )}

            {Number(r.discountPercent ?? 0) > 0 && !r.priceOverride && (
              <div className="bg-green-50 border border-green-200 rounded-xl px-3 py-2">
                <p className="text-sm font-medium text-green-800">Скидка: {Number(r.discountPercent)}%</p>
              </div>
            )}

            {r.managerNotes && (
              <div className="bg-orange-50 border border-orange-200 rounded-xl px-3 py-2">
                <p className="text-xs text-orange-700 whitespace-pre-line">⚠️ {r.managerNotes}</p>
              </div>
            )}

            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); confirm.mutate(r.id) }}
              disabled={confirm.isPending}
              className="w-full mt-2 bg-brand-600 text-white rounded-xl py-2.5 text-sm font-medium disabled:opacity-60 hover:bg-brand-700 transition-colors"
            >
              {confirm.isPending ? 'Подтверждаем...' : 'Подтверждено'}
            </button>
          </div>
          )
        })}
      </div>
    </div>
  )
}
